import React from 'react';
import { Card, CardContent, Typography, Box, Grid, LinearProgress } from '@mui/material';
import { CheckCircle, Error, Warning, Info } from '@mui/icons-material';
import './DataQualityDashboard.css';
import { macroNames } from '../styles/theme';

const DataQualityDashboard = ({ data, type }) => {
  // Función para verificar si una receta tiene datos completos
  const isRecipeComplete = (recipe) => {
    return (
      recipe.ingredients && recipe.ingredients.length > 0 &&
      recipe.tipoDeCocina && recipe.tipoDeCocina.length > 0 &&
      recipe.tipoPlato && recipe.tipoPlato.length > 0 &&
      ((Array.isArray(recipe.momentoDelDia) && recipe.momentoDelDia.length > 0) || (!!recipe.momentoDelDia)) &&
      recipe.mainImage
    );
  };

  // Función para verificar si un ingrediente tiene datos completos
  const isIngredientComplete = (ingredient) => {
    return (
      ingredient.kilocalories !== undefined && ingredient.kilocalories !== null &&
      ingredient.carbs !== undefined && ingredient.carbs !== null &&
      ingredient.protein !== undefined && ingredient.protein !== null &&
      ingredient.fat !== undefined && ingredient.fat !== null &&
      ingredient.gramsPerUnit !== undefined && ingredient.gramsPerUnit !== null &&
      ingredient.categoria &&
      ingredient.subcategoria
    );
  };

  // Calcular estadísticas
  const totalItems = data.length;
  const reviewedItems = data.filter(item => item.revisado).length;
  const unreviewed = totalItems - reviewedItems;

  const completeItems = type === 'recipes'
    ? data.filter(isRecipeComplete).length
    : data.filter(isIngredientComplete).length;
  const incompleteItems = totalItems - completeItems;

  // Calcular porcentajes
  const reviewedPercentage = totalItems > 0 ? (reviewedItems / totalItems) * 100 : 0;
  const completePercentage = totalItems > 0 ? (completeItems / totalItems) * 100 : 0;

  // Determinar color y estado general
  const getQualityStatus = () => {
    const avgQuality = (reviewedPercentage + completePercentage) / 2;
    if (avgQuality >= 80) return { status: 'excellent', color: 'success', icon: CheckCircle };
    if (avgQuality >= 60) return { status: 'good', color: 'warning', icon: Warning };
    if (avgQuality >= 40) return { status: 'fair', color: 'warning', icon: Warning };
    return { status: 'poor', color: 'error', icon: Error };
  };

  const qualityStatus = getQualityStatus();
  const StatusIcon = qualityStatus.icon;

  const getStatusMessage = () => {
    switch (qualityStatus.status) {
      case 'excellent': return `Excelente calidad de datos (${Math.round((reviewedPercentage + completePercentage) / 2)}%)`;
      case 'good': return `Buena calidad de datos (${Math.round((reviewedPercentage + completePercentage) / 2)}%)`;
      case 'fair': return `Calidad de datos mejorable (${Math.round((reviewedPercentage + completePercentage) / 2)}%)`;
      case 'poor': return `Calidad de datos baja (${Math.round((reviewedPercentage + completePercentage) / 2)}%)`;
      default: return 'Evaluando calidad de datos...';
    }
  };

  return (
    <div className="data-quality-dashboard">
      <Card className="dashboard-card">
        <CardContent>
          {/* Header del Dashboard */}
          <Box className="dashboard-header" sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <StatusIcon color={qualityStatus.color} />
              <Typography variant="h6" className="dashboard-title">
                Calidad de Datos
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" className="dashboard-subtitle">
              {getStatusMessage()}
            </Typography>
          </Box>

          {/* Métricas Principales */}
          <Grid container spacing={3}>
            {/* Total de elementos */}
            <Grid item xs={6} sm={3}>
              <Box className="metric-card">
                <Typography variant="h4" className="metric-value">
                  {totalItems}
                </Typography>
                <Typography variant="body2" className="metric-label">
                  Total {type === 'recipes' ? 'Recetas' : 'Ingredientes'}
                </Typography>
              </Box>
            </Grid>

            {/* Elementos revisados */}
            <Grid item xs={6} sm={3}>
              <Box className="metric-card">
                <Typography variant="h4" className="metric-value metric-value-success">
                  {reviewedItems}
                </Typography>
                <Typography variant="body2" className="metric-label">
                  Revisados
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={reviewedPercentage}
                    className="metric-progress metric-progress-success"
                    sx={{ flexGrow: 1, height: 4 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {Math.round(reviewedPercentage)}%
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Elementos no revisados */}
            <Grid item xs={6} sm={3}>
              <Box className="metric-card">
                <Typography variant="h4" className="metric-value metric-value-warning">
                  {unreviewed}
                </Typography>
                <Typography variant="body2" className="metric-label">
                  Sin revisar
                </Typography>
              </Box>
            </Grid>

            {/* Elementos completos */}
            <Grid item xs={6} sm={3}>
              <Box className="metric-card">
                <Typography variant="h4" className="metric-value metric-value-info">
                  {completeItems}
                </Typography>
                <Typography variant="body2" className="metric-label">
                  Datos completos
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={completePercentage}
                    className="metric-progress metric-progress-info"
                    sx={{ flexGrow: 1, height: 4 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {Math.round(completePercentage)}%
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Detalles adicionales */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <Info fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
              Campos requeridos para datos completos:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {type === 'recipes'
                ? 'Ingredientes, Tipo de cocina, Tipo de plato, Momento del día, Imagen'
                : `${macroNames.kilocalories}, ${macroNames.carbs}, ${macroNames.protein}, ${macroNames.fat}, Gramos por unidad, Categoría, Subcategoría`
              }
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};

export default DataQualityDashboard; 